/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {GenericObject} from '@ion/core/src/types/entities';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {getDefaultParameters} from '@ion/core/src/utils/QueryStringUtils';

/**
 * Defines the return type for the useEnvQueryParam hook, which is provided
 * as an object containing whichever `DEFAULT_QUERY_PARAMS` (e.g. `environment`,
 * `oidcEnabled`, `features`) are currently present in the application query
 * string. If the application query string currently does not contain any of
 * those parameters, an empty object is returned.
 */
export type UseEnvQueryParamResult = GenericObject | {};

/**
 * Provides a hook which returns the current state of the default query
 * parameters (see `DEFAULT_QUERY_PARAMS`). If the application querystring
 * currently contains a matching parameter, it will be returned as a
 * serialized object, for example when the application's query string
 * contains `?environment=us`, then this hook will return an object in the
 * form of `{environment: 'us'}`. If the application's querystring does not
 * contain a matching parameter, then an empty object will be returned.
 *
 * ```typescript
 *   import useEnvQueryParam from '@ion/applications/src/hooks/useEnvQueryParam';
 *
 *   // https://app.calamp.com/some-page?environment=us
 *   const env = useEnvQueryParam(retainEnvQueryParam); // {environment: 'us'} | {}
 *   const qs  = getQueryString({
 *     ...env,
 *     id   : item.id,
 *     date : new Date(item.timestamp)
 *   });
 *   const url = `/some-other-page?${qs}`;
 *   // https://app.calamp.com/some-other-page?environment=us&id=<id>&date=<date>
 * ```
 *
 * @param   retain when `true` (default) indicates that the default query
 *          parameters are to be returned, otherwise, when `false`, an empty
 *          object will be returned.
 * @returns UseEnvQueryParamResult
 */
export const useEnvQueryParam = (retain = true): UseEnvQueryParamResult => {
  if (retain) {
    const result = getDefaultParameters();
    if (!isObjectEmpty(result)) {
      return result;
    }
  }
  return {};
};
export default useEnvQueryParam;
